import { NumberSymbol } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { User } from '../models/user.model';
import { ImportProMappedHeadersProfile } from '../models/import-pro-mapped-headers-profile'
import { PartnerPro } from '../models/partner-pro'
import { ProLabelMappingProfile } from '../models/pro-label-mapping-profile'
import { MappedHeader } from '../models/mapped-header';
import { Router } from '@angular/router';
import { Episode } from '../models/episode';
import { Show } from '../models/show';
import { Season } from '../models/season';
import { ShowMapperProfile } from '../models/show-mapper-profile';
import { PartnerGroupMappedShow } from '../models/partner-group-mapped-show';
import { of } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImportService {

  importUrl = environment.apiURL + '/api/v1/admin/import/';
  exportUrl = environment.apiURL + '/api/v1/admin/export/';
  proUrl = environment.apiURL + '/api/v1/admin/pro/';
  episodeUrl = environment.apiURL + '/api/v1/admin/episode/';
  seasonUrl = environment.apiURL + '/api/v1/admin/season/';
  showMapperUrl = environment.apiURL + '/api/v1/admin/show/mapping-profile/'

  //this array is used for validation when assigning labels
  //it will help ensure that the assigned label is appropriate
  catLabelDataTypes = [
    { label: 'DISTRIBUTION YEAR', type: 'number' },
    { label: 'DISTRIBUTION QUARTER', type: 'string' },
    { label: 'STATEMENT RECIPIENT ID', type: 'number' },
    { label: 'STATEMENT RECIPIENT NAME', type: 'string' },
    { label: 'PARTY ID', type: 'number' },
    { label: 'PARTY NAME', type: 'string' },
    { label: 'LEGAL EARNER PARTY ID', type: 'number' },
    { label: 'LEGAL EARNER PARTY NAME', type: 'string' },
    { label: 'PERFORMANCE SOURCE', type: 'string' },
    { label: 'MUSIC USER GENRE', type: 'string' },
    { label: 'MUSIC USER', type: 'string' },
    { label: 'NETWORK SERVICE', type: 'string' },
    { label: 'PERFORMANCE START DATE', type: 'Date' },
    { label: 'PERFORMANCE END DATE', type: 'Date' },
    { label: 'SURVEY TYPE', type: 'string' },
    { label: 'DAY PART CODE', type: 'number' },
    { label: 'SERIES OR FILM/ATTRACTION', type: 'string' },
    { label: 'PROGRAM/EPISODE NAME', type: 'string' },
    { label: 'WORK ID', type: 'number' },
    { label: 'WORK TITLE', type: 'string' },
    { label: 'CA%', type: 'number' },
    { label: 'CLASSIFICATION CODE', type: 'number' },
    { label: 'NUMBER OF PLAYS', type: 'number' },
    { label: 'PERFORMANCE TYPE', type: 'string' },
    { label: 'DURATION', type: 'Time' },
    { label: 'PERFORMING ARTIST', type: 'string' },
    { label: 'COMPOSER NAME', type: 'string' },
    { label: 'EE SHARE', type: 'number' },
    { label: 'CREDITS', type: 'number' },
    { label: 'DOLLARS', type: 'number' },
    { label: 'PREMIUM CREDITS', type: 'number' },
    { label: 'PREMIUM ROYALTY', type: 'number' },
    { label: 'ADJUSTMENT INDICATOR', type: 'string' },
    { label: 'ADJUSTMENT REASON CODE', type: 'number' },
    { label: 'ORIGINAL DISTRIBUTION DATE', type: 'Date' },
    { label: 'ROLE TYPE', type: 'string' },
    { label: 'TYPE OF RIGHT', type: 'string' },
    { label: 'TERRITORY', type: 'string' },
    { label: 'LICENSOR', type: 'string' },
    { label: 'PROGRAM CODE', type: 'number' },
    { label: 'PERFORMANCE QUARTER', type: 'string' },
    { label: 'COUNTRY NAME', type: 'string' },
    { label: 'FILE TYPE', type: 'string' },
    { label: 'LICENSOR CODE', type: 'string' },
    { label: 'REVENUE CLASS CODE', type: 'number' },
    { label: 'REVENUE CLASS DESCRIPTION', type: 'string' },
    { label: 'ADJUSTMENT DISTRIBUTION DATE', type: 'Date' },
    { label: 'CURRENT ACTIVITY AMOUNT', type: 'number' },
    { label: 'HIT SONG BONUS', type: 'number' },
    { label: 'STANDARDS BONUS', type: 'number' },
    { label: 'FOREIGN SOCIETY ADJUSTMENT', type: 'string' },
    { label: 'PERFORMANCE DATE', type: 'Date' },
    { label: 'W OR P', type: 'string' },
    { label: 'SHOW NAME', type: 'string' },
    { label: 'PAYMENT TYPE', type: 'string' },
    { label: 'CHART', type: 'string' },
    { label: 'PAY NUMBER', type: 'number' },
    { label: 'SYND', type: 'string' },
    { label: 'STRIP', type: 'string' },
    { label: 'PERFORMANCE', type: 'number' },
    { label: 'OCCUR', type: 'number' },
    { label: 'SOCIETY ABBRV', type: 'string' },
    { label: 'SOCIETY COUNTRY', type: 'string' }
  ];
  //IMPORT TYPE OPTIONS
  importTypes = ["Source Audio", "ASCAP DP", "ASCAP IP", "BMI", "Sesac"];

  //IMPORT YEAR OPTIONS
  importYears = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016];

  //IMPORT QUARTERS
  importQuarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  //BREADCRUMBS
  breadcrumbs = [
    { title: 'IMPORT', value: 1 },
    { title: 'MAP', value: 2 },
    { title: 'SUBMIT', value: 3 }
  ];


  //TEST DATA - these represent the approved labels that point the mibe stored data
  //This will be the labels of which MIBE is familiar with.
  // approvedCatLabels = ['header 1', 'header 2', 'header 3', 'header 4', 'header 5'];

  //all properties (except id) from PartnerPro table in database
  private approvedCatLabelsSource = new ReplaySubject<string[]>(1);
  approvedCatLabels$ = this.approvedCatLabelsSource.asObservable();


  private breadCrumbActiveStepSource = new ReplaySubject<number | null>(1);
  breadCrumbActiveStep$ = this.breadCrumbActiveStepSource.asObservable();
  _ = this.breadCrumbActiveStepSource.next(null);

  //PRE IMPORT SETTINGS
  private importTypeSource = new ReplaySubject<string>(1);
  importType$ = this.importTypeSource.asObservable();

  private importYearSource = new ReplaySubject<number>(1);
  importYear$ = this.importYearSource.asObservable();

  private importQuarterSource = new ReplaySubject<string>(1);
  importQuarter$ = this.importQuarterSource.asObservable();

  private importIdSource = new ReplaySubject<number>(1);
  importId$ = this.importIdSource.asObservable();

  //represents the file path for the file stored on api server
  private importedFilePathSource = new ReplaySubject<string>(1);
  importedFilePath$ = this.importedFilePathSource.asObservable();

  private importedHeadersSource = new ReplaySubject<string[]>(1);
  importedHeaders$ = this.importedHeadersSource.asObservable();


  private importedDateSource = new ReplaySubject<string>(1);
  importedDate$ = this.importedDateSource.asObservable();

  private importerNameSource = new ReplaySubject<string>(1);
  importerName$ = this.importerNameSource.asObservable();

  // Internal state to track if variables are set
  private isImportTypeSet = false;
  private isImportYearSet = false;
  private isImportQuarterSet = false;

  //allows the pro-import view to be displayed
  private showProImportSource = new ReplaySubject<boolean>(1);
  showProImport$ = this.showProImportSource.asObservable();


  //tracks mapped labels - SUPER ADMIN only
  private mappedCategoryLabelsSource = new ReplaySubject<MappedHeader[]>(1);
  mappedCategoryLabels$ = this.mappedCategoryLabelsSource.asObservable();


  //allows header mapper view to be shown for super admin
  private showHeaderMapperSource = new ReplaySubject<boolean>(1);
  showHeaderMapper$ = this.showHeaderMapperSource.asObservable();

  //allows ReadyToMap view to be shown
  private showReadyToMapSource = new ReplaySubject<boolean>(1);
  showReadyToMap$ = this.showReadyToMapSource.asObservable();

  //tracks the importProMappedHeadersProfile after label mapping is complete
  private importProMappedHeadersProfileSource = new ReplaySubject<ImportProMappedHeadersProfile>(1);
  importProMappedHeadersProfile$ = this.importProMappedHeadersProfileSource.asObservable();


  //tracks mapped partners and shows
  private mappedPartnerProsSource = new ReplaySubject<any[]>(1);
  mappedPartnerPros$ = this.mappedPartnerProsSource.asObservable();


  //tracks saved label mapping profile for selected pro type on import - nullable for cases where profile hasn't been created yet
  private savedProLabelMappingProfileSource = new ReplaySubject<ProLabelMappingProfile | null>(1);
  savedProLabelMappingProfile$ = this.savedProLabelMappingProfileSource.asObservable();


  //stores progress on the show mapper view
  private showMapperProfileSource = new ReplaySubject<ShowMapperProfile>(1);
  showMapperProfile$ = this.showMapperProfileSource.asObservable();

  importExists = false;

  constructor(private http: HttpClient, private router: Router) { }


  //PRO IMPORT DETAILS - START - TRACKS TYPE, YEAR AND QUARTER FOR IMPORT
  setImportType(importType: string) {
    this.importTypeSource.next(importType);
    this.isImportTypeSet = true;
  }

  setImportYear(importYear: number) {
    this.importYearSource.next(importYear);
    this.isImportYearSet = true;
  }

  setImportQuarter(importQuarter: string) {
    this.importQuarterSource.next(importQuarter);
    this.isImportQuarterSet = true;
  }

  setImportId(id: number) {
    this.importIdSource.next(id);
  }
  //PRO IMPORT DETAILS - END


  //ImportProMappedHeadersProfile - START
  setImportProMappedHeadersProfile(profile: ImportProMappedHeadersProfile) {
    this.importProMappedHeadersProfileSource.next(profile);
  }
  //ImportProMappedHeadersProfile - END



  //IMPORTED FILE DETAILS - START - TRACKS UNIQUE DETAILS ABOUT THE IMPORTED FILE

  //HEADERS - KEEPS TRACK OF THE HEADERS FROM THE CURRENTLY WORKING IMPORTED FILE
  setImportedHeaders(headers: string[]) {
    this.importedHeadersSource.next(headers);
  }

  //FILE PATH - TRACKS THE FILE PATH ON THE API SERVER TO ACCESS TO THE IMPORTED FILE
  setImportedFilePath(path: string) {
    this.importedFilePathSource.next(path);
  }

  //IMPORTED DATE - TRACKS THE DATE THE FILE WAS IMPORTED/UPLOADED IN STRING FORMAT
  setImportedDate(date: string) {
    this.importedDateSource.next(date);
  }

  //IMPORTER NAME - TRACKS THE IMPORTER BY FIRST NAME AND LAST NAME INITIAL.  i.e. Robert H.
  setImporterName(name: string) {
    this.importerNameSource.next(name);
  }

  //IMPORTED FILE DETAILS END




  //SUB NAV PROPERTIES - START - USED TO HELP DETERMINE WHAT VIEWS OR PORTIONS OF VIEWS ARE DISPLAYED ON data-pro.component

  setShowProImport(value: boolean) {
    this.showProImportSource.next(value);
  }

  setShowHeaderMapper(value: boolean) {
    this.showHeaderMapperSource.next(value);

    //turn off previous step
    this.setShowProImport(false);
  }

  setShowReadyToMap(value: boolean) {
    this.showReadyToMapSource.next(value);

    //turn of previous step
    this.setShowHeaderMapper(false);
  }

  //SUB NAV PROPERTIES - END



  //CATEGORY LABELS - START

  setMappedCategoryLabels(mappedHeaders: MappedHeader[]) {

    this.mappedCategoryLabelsSource.next(mappedHeaders);

  }

  setApprovedCategoryLabels(approvedLabels: string[]) {
    this.approvedCatLabelsSource.next(approvedLabels);
  }
  //CATEGORY LABELS - END


  //MAPPED PARTNERPROS- START

  setMappedParterPros(values: any[]) {
    this.mappedPartnerProsSource.next(values)
  }

  //MAPPED PARTNERPROS - EMD

  /*
* Function to check if all import variables are set
*/
  areAllImportVariablesSet(): boolean {

    this.setShowProImport(this.isImportTypeSet && this.isImportYearSet && this.isImportQuarterSet);

    return this.isImportTypeSet && this.isImportYearSet && this.isImportQuarterSet;
  }


  /**
   * Used to keep track of which step the user is on for the PRO import
   * @param step 
   */
  setBreadCrumbActiveStep(step: NumberSymbol) {
    this.breadCrumbActiveStepSource.next(step);
  }



  /**
   * Stores the Importer Details i.e. "Sean C., 7/2/2024"
   * @param user 
   */
  setImporterDetails(user: any) {

    //extract user first name and last name initial
    var displayName = `${user.first_name} ${user.last_name[0]}.`;


    //BELOW LOGIC MAY NEED TO BE RELOCATED TO THE ACTUAL UPLOAD PORTION OF THIS PROCESS - LEAVING HERE FOR NOW
    //create string value to represent upload date
    var today = new Date();
    const month = today.getMonth() + 1; // Months are zero-based, so we add 1
    const day = today.getDate();
    const year = today.getFullYear();

    // // Ensure single-digit months and days are prefixed with a zero
    // const monthString = month < 10 ? '0' + month : month.toString();
    // const dayString = day < 10 ? '0' + day : day.toString();

    var displayDate = `${month}/${day}/${year}`;


    this.setImportedDate(displayDate);
    this.setImporterName(displayName);
  }


  setSavedProLabelMappingProfile(profile: ProLabelMappingProfile | null) {
    this.savedProLabelMappingProfileSource.next(profile);
  }


  setShowMapperProfile(profile: ShowMapperProfile){
    this.showMapperProfileSource.next(profile);
  }


  /**
   * resets all values from this file which essentially starts
   * the import process over
   */
  resetImportProcess() {
    this.setImportType(null);
    this.setImportYear(null);
    this.setImportQuarter(null);

    this.setImportedHeaders(null);
    this.setImportedFilePath(null);
    this.setImportedDate(null);
    this.setImporterName(null);

    this.setShowReadyToMap(false); //this will reset all other view control variables

    this.setBreadCrumbActiveStep(null);
    this.setSavedProLabelMappingProfile(null);
  }


  /**
   * Saves the import.
   * This function should only be called by super admin
   * after all category labels have been assigned
   */
  saveProImport() {
    console.log("Saving Pro Import");
  }

  /**
   * This will allow the user to save their data mapping progress
   */
  saveProMapping() {
    console.log("Saving Pro Mapping");
  }



  //HTTP REQUESTS - Start

  /**
 * Used to upload .csv or .xlsx file for PRO Import
 */
  uploadPRO(selectedFile: File, importType: string, year: number, quarter: string, userId: number) {

    const formData = new FormData();
    formData.append('file', selectedFile, selectedFile.name);
    formData.append('importType', importType);
    formData.append('year', year.toString());
    formData.append('quarter', quarter);
    formData.append('userId', userId.toString());

    return this.http.post<Array<string>>(this.importUrl + 'import-pro', formData);
  }

  getImportedHeaders(importId: number) {

    const params = new HttpParams().set('importId', importId.toString());

    return this.http.get(this.importUrl + 'get-headers', { params })
  }

  
  /**
 * Saves mapped category labels to the headers from the imported excel sheet.
 * This function should only be called by super admin
 * after all category labels have been assigned
 */
  saveMappingProfile(mappedHeaders: MappedHeader[], filePath: string, userId: number) {
    //we need to send the mapping profile, the file path and the user id to the api for storage
    const formData = new FormData();
    formData.append('mappedHeaders', JSON.stringify(mappedHeaders));
    formData.append('filePath', filePath);
    formData.append('userId', userId.toString());

    const call = this.http.post<ImportProMappedHeadersProfile>(this.importUrl + 'store-mapped-headers', formData).pipe(
      map((response: any) => ({
        id: response.id,
        filePath: response.file_path,
        createdById: response.user_id
      }))
    );

    return call;
  }

  getApprovedCatLabels() {
    return this.http.get<string[]>(this.importUrl + 'get-approved-cat-labels');
  }


  getImportProMappedHeadersProfile(id: number) {
    const params = new HttpParams().set('id', id.toString());

    return this.http.get<any>(this.importUrl + 'get-mapped-headers-profile', { params }).pipe(
      map((response: any) => {
        return {
          id: response.id,
          filePath: response.file_path,
          createdById: response.created_by,
          importedHeaders: response.imported_headers
        }
      })
    );

  }

  deleteImport(id: number) {
    return this.http.delete<any>(this.importUrl + 'delete-import-details/' + id);
  }

  //get Import details for mapping partner groups to already mapped shows
  // 1/7/25 GW - removed params to get a more general data pull (at least for now)
  getImportDetailsMappedShows(profileId: number, page: number, search: string = '', searchType: string = '') {
  // getImportDetailsMappedShows(page: number, search: string = '', searchType: string = '') {

    const params = new HttpParams().set('id', profileId.toString()).set('page', page.toString()).set('search', search).set('searchType', searchType);

    // return this.http.get<PartnerPro[]>(this.importUrl + 'get-import-details-mapped-shows').pipe(
    return this.http.get<PartnerPro[]>(this.importUrl + 'get-import-details-mapped-shows', { params }).pipe(
      map((response: any[]) => {
        return response['partner_pros'].map(item => {
          // Return the mapped PartnerPros
          return PartnerPro.mapProperties(item);
        })
      })
    );
  
  }
  
  //Get Import details for show mapping
  getImportDetails(profileId: number, page: number, search: string = '', searchType: string = '') {

    const params = new HttpParams().set('id', profileId.toString()).set('page', page.toString()).set('search', search).set('searchType', searchType);

    return this.http.get<PartnerPro[]>(this.importUrl + 'get-import-details', { params }).pipe(
      map((response: any[]) => {
        return response['partner_pros'].map(item => {
          // Return the mapped PartnerPros
          return PartnerPro.mapProperties(item);
        })
      })
    );
    
    // Below code is for paginated responses from the server
    // return this.http.get<{ count: number, next: string | null, previous: string | null, results: any }>(this.importUrl + 'get-import-details', { params }).pipe(
    //   map((response: any) => {
    //     // // Ensure 'results' exists and extract 'partner_pros' from it
    //     // const results = response.results;
    //     // if (results && results.partner_pros) {
    //     //   return {
    //     //     results: results.partner_pros.map((item: any) => {
    //     //       // Return the mapped PartnerPros
    //     //       return PartnerPro.mapProperties(item);
    //     //     }),
    //     //     count: response.count
    //     //     // returns object like:
    //     //     // {
    //     //     //    results: [...]
    //     //     //    count: #
    //     //     // }
    //     // }
    //     // } else {
    //     //   // Handle cases where 'partner_pros' is missing or results is undefined
    //     //   return [];
    //     // }
    //   })
    // );
  }

  /**
   * Creates or updates label mapping profile for specified pro type
   * @param profile 
   */
  upsertMappingProfile(profile: ProLabelMappingProfile) {
    const url = `${this.proUrl}mapping-profile/upsert/`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<ProLabelMappingProfile>(url, profile, httpOptions).pipe(
      map((response: any) => {
        // Return the mapped ProLabelMappingProfile
        return ProLabelMappingProfile.mapProperties(response);
      })
    )
  }

  /**
   * Returns saved label mapping profile if one exist for pro type
   * @param proType 
   * @returns 
   */
  getSavedMappingProfile(proType: string) {
    const params = new HttpParams().set('pro_type', proType);

    return this.http.get<ProLabelMappingProfile | null>(`${this.proUrl}mapping-profile/by-pro-type`, { params }).pipe(
      map((response: any) => {
        if (response) {
          return ProLabelMappingProfile.mapProperties(response);
        }
        else {
          //profile hasn't been created yet
          return null;
        }
      })
    )
  }



  exportPartnerPros(ids: number[], exportType: string, fileName: string, partnerId: any = null, includedFilterCategoriesNames: any = null, excludedFilterCategoriesNames: any = null) {
    const url = `${this.exportUrl}export-pros`

    // Prepare the body data
    const body = {
      partnerProIds: ids,
      exportType: exportType,
      fileName: fileName,
      partnerId: partnerId,
      includedFilterCategoriesNames: includedFilterCategoriesNames,
      excludedFilterCategoriesNames: excludedFilterCategoriesNames
    };

    // Set responseType to 'blob' to handle file download
    let call = this.http.post(url, body, { responseType: 'blob' });
    call.subscribe(
      (response: Blob) => {
        // Create a link element and trigger a download
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(response);
        a.href = objectUrl;
        a.download = `${fileName}.${exportType}`;
        a.click();
        URL.revokeObjectURL(objectUrl);
      },
      error => {
        console.error('Export failed', error);
      }
    );

    return call;
  }





  //THE BELOW API CALLS EITHER NEED TO BE CREATED AND/OR TESTED

  createMappedEpisode(episode: Episode, unrecognizedShowName: string) {
    const url = `${this.episodeUrl}mapping/post-mapped-episode/`

    // Prepare the body data
    const body = {
      episode_id: episode.id,
      unrecognized_episode_name: unrecognizedShowName
    };

    return this.http.post(url, body);
  }

  getMappedEpisodeByUnrecognizedName(showId: number, unrecognizedName: string) {
    const url = `${this.episodeUrl}mapping/get-mapped-episode/`

    const params = new HttpParams().set('show_id', showId.toString())
      .set('unrecognized_episode_name', unrecognizedName);


    return this.http.post(url, { params }).pipe(
      map((response: any) => {
        return response;
      }
      )
    )
  }

  getMappedEpisodesForShow(showId: number){
    const url = `${this.episodeUrl}mapping/get-mapped-episodes`

    const params = new HttpParams().set('show_id', showId.toString());

    return this.http.get(url, { params }).pipe(
      map((response: any) => {
        return response;
      }
      )
    )
  }

  createNewSeason(show: Show, seasonName: string, seasonNumber: number) {

    const url = `${this.seasonUrl}post-season/`

    // Prepare the body data
    const body = {
      show_id: show.id,
      season_name: seasonName,
      season_number: seasonNumber
    };

    //will probably have to map the response
    return this.http.post<Season>(url, body);
  }

  createNewEpisode(season: Season, episodeName: string, episodeNumber: number) {
    const url = `${this.episodeUrl}post-episode/`

    // Prepare the body data
    const body = {
      season_id: season.id,
      episode_name: episodeName,
      episode_number: episodeNumber
    };

    //will probably have to map the response
    return this.http.post<Episode>(url, body);
  }

  createShowMapperProfile(profile: ShowMapperProfile){
    const url = `${this.showMapperUrl}upsert-show-mapper-profile/`

    return this.http.post<ShowMapperProfile>(url, profile);

  }

  /**
   * Validates the label being mapped
   * @param importId 
   * @param mappedTo 
   * @param header 
   */
  checkDataType(importId: number, mappedTo: string, header: string){

    const url = `${this.importUrl}check-data-type/`

    const params = new HttpParams()
    .set('import_id', importId.toString())
    .set('mapped_to', mappedTo)
    .set('header', header)

    return this.http.get<boolean>(url, {params});
  }

  setImportExists( trueOrFalse: boolean ) {
    this.importExists = trueOrFalse;
  }

  getImportDetailsList() {
    const url = `${this.importUrl}get-import-details-list`;

    return this.http.get<any>(url);
  }
}
